/**
 * NotificationContext.js - Gerenciamento Centralizado de Notificações
 * 
 * ARQUITETURA "WHATSAPP WEB" — FASE B (Supabase Realtime):
 * - 1 único listener para Supabase Realtime (notifications)
 * - 1 único listener para Supabase Realtime (user_status)
 * - Distribui eventos para todos os componentes via Context
 * 
 * MIGRAÇÃO: RTDB → Supabase Realtime (elimina Firebase Realtime Database)
 */

import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { auth } from '../firebase';
import { useCompany } from './CompanyContext';
import { subscribeToNotifications, subscribeToUserStatuses } from '../utils/supabaseRealtime';
import { logger } from '../utils/logger';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { selectedCompany } = useCompany();
  const [notifications, setNotifications] = useState({});
  const [newMessages, setNewMessages] = useState({});
  const [userStatuses, setUserStatuses] = useState({});
  const [typingStatuses, setTypingStatuses] = useState({});
  
  const notificationUnsubscribeRef = useRef(null);
  const statusUnsubscribeRef = useRef(null);
  
  // ============================================
  // LISTENER 1: Notificações (Supabase Realtime)
  // ============================================
  useEffect(() => {
    if (!selectedCompany?.companyId || !auth.currentUser) {
      if (notificationUnsubscribeRef.current) {
        notificationUnsubscribeRef.current();
        notificationUnsubscribeRef.current = null;
      }
      setNotifications({});
      setNewMessages({});
      return;
    }
    
    logger.log('[NotificationContext] Iniciando listener Supabase de notificações:', selectedCompany.companyId);
    
    let cancelled = false;
    let unsub = null;

    (async () => {
      try {
        unsub = await subscribeToNotifications({
          companyId: selectedCompany.companyId,
          userId: auth.currentUser.uid,
          onChange: (notifMap) => {
            if (cancelled) return;
            logger.log('[NotificationContext] Notificações recebidas:', Object.keys(notifMap).length);
            setNotifications(notifMap);
            
            const newMsgs = {};
            Object.entries(notifMap).forEach(([contactId, data]) => {
              if (data && data.unreadCount > 0) {
                newMsgs[contactId] = {
                  lastMessage: data.lastMessage,
                  unreadCount: data.unreadCount,
                  timestamp: data.timestamp || Date.now(),
                  contactName: data.contactName || null,
                };
              }
            });
            
            setNewMessages(prev => {
              const hasChanges = Object.keys(newMsgs).some(id => 
                !prev[id] || prev[id].unreadCount !== newMsgs[id].unreadCount
              );
              return hasChanges ? newMsgs : prev;
            });
          },
        });

        if (cancelled && unsub) {
          unsub();
        }
      } catch (err) {
        logger.warn('[NotificationContext] Erro ao iniciar subscription de notifications:', err);
      }
    })();
    
    notificationUnsubscribeRef.current = () => {
      cancelled = true;
      if (unsub) { try { unsub(); } catch {} }
    };

    return () => {
      if (notificationUnsubscribeRef.current) {
        notificationUnsubscribeRef.current();
        notificationUnsubscribeRef.current = null;
      }
    };
  }, [selectedCompany?.companyId, auth.currentUser?.uid]);
  
  // ============================================
  // LISTENER 2: Status de Usuários (Supabase Realtime)
  // ============================================
  useEffect(() => {
    if (!selectedCompany?.companyId || !auth.currentUser) {
      if (statusUnsubscribeRef.current) {
        statusUnsubscribeRef.current();
        statusUnsubscribeRef.current = null;
      }
      setUserStatuses({});
      setTypingStatuses({});
      return;
    }
    
    logger.log('[NotificationContext] Iniciando listener Supabase de user_status');
    
    let cancelled = false;
    let unsub = null;

    (async () => {
      try {
        unsub = await subscribeToUserStatuses({
          companyId: selectedCompany.companyId,
          onChange: (statusMap) => {
            if (cancelled) return;

            logger.log('[NotificationContext] Status de usuários recebidos:', Object.keys(statusMap).length);
            setUserStatuses(statusMap);
            
            const typing = {};
            Object.entries(statusMap).forEach(([userId, status]) => {
              if (status.typing && status.typingTo === auth.currentUser?.uid) {
                typing[userId] = true;
              }
            });
            setTypingStatuses(typing);
          },
        });

        if (cancelled && unsub) {
          unsub();
        }
      } catch (err) {
        logger.warn('[NotificationContext] Erro ao iniciar subscription de user_status:', err);
      }
    })();
    
    statusUnsubscribeRef.current = () => {
      cancelled = true;
      if (unsub) { try { unsub(); } catch {} }
    };
    
    return () => {
      if (statusUnsubscribeRef.current) {
        statusUnsubscribeRef.current();
        statusUnsubscribeRef.current = null;
      }
    };
  }, [selectedCompany?.companyId, auth.currentUser?.uid]);
  
  // ============================================
  // FUNÇÕES PÚBLICAS
  // ============================================
  
  const clearNotification = useCallback((contactId) => {
    setNewMessages(prev => {
      const updated = { ...prev };
      delete updated[contactId];
      return updated;
    });
  }, []);
  
  const isUserOnline = useCallback((userId) => {
    return userStatuses[userId]?.online === true;
  }, [userStatuses]);
  
  const isUserTyping = useCallback((userId) => {
    return typingStatuses[userId] === true;
  }, [typingStatuses]);
  
  const getUserLastSeen = useCallback((userId) => {
    return userStatuses[userId]?.lastSeen || null;
  }, [userStatuses]);
  
  const value = useMemo(() => ({
    notifications,
    newMessages,
    userStatuses,
    typingStatuses,
    clearNotification,
    isUserOnline,
    isUserTyping,
    getUserLastSeen
  }), [notifications, newMessages, userStatuses, typingStatuses, clearNotification, isUserOnline, isUserTyping, getUserLastSeen]);
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de NotificationProvider');
  }
  return context;
};

export default NotificationContext;
